import React, { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import images from '../../app/utlis/image'
import { fetchBlogs, getPageData, useData } from '../../app/helper/functions';
import Heading from '../atoms/Heading/Heading';
import Footer from '../organisms/Footer/Footer';
import ContactHelp from '../molecules/ContactHelp/ContactHelp';
import 'react-tabs/style/react-tabs.css';
import { Blog } from '../../app/utlis/type';
import { categoryOptions } from '../../app/utlis/options';
import { Link } from 'react-router-dom';
import CompanyStats from '../molecules/CompanyStats/CompanyStats';

const BlogPage = () => {
    const { data } = useData();
    const blogData = getPageData(data, 'blogPage');
    const [activeTab, setActiveTab] = useState('All')
    const [loading, setLoading] = useState(false)
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [filterBlogs, setFilterBlogs] = useState<Blog[]>([])
    const yellowRef = useRef<HTMLDivElement>(null);
    const greenRef = useRef<HTMLDivElement>(null);
    const purpleRef = useRef<HTMLDivElement>(null);

    const formatDate = (timestamp: any): string => {
        if (!timestamp) return '';

        let date: Date;

        if (timestamp?.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else if (timestamp?.toDate) {
            date = timestamp.toDate();
        } else {
            date = new Date(timestamp);
        }

        if (Number.isNaN(date.getTime())) return '';

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    useEffect(() => {
        if (activeTab === 'All') {
            setFilterBlogs(blogs)
        } else {
            setFilterBlogs(blogs.filter((blog) => blog.categories.includes(activeTab)))
        }
    }, [activeTab, blogs])

    useEffect(() => {
        try {
            setLoading(true);
            fetchBlogs().then((res) => {
                setBlogs(res)
                setFilterBlogs(res)
            })
        } catch (err) {
            setLoading(false);
        }
        finally {
            setLoading(false);
        }
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const buttonVariants = {
        hover: { scale: 1.05 },
        tap: { scale: 0.95 }
    };

    return (
        <div>
            <div className='bg-blog-bg bg-center bg-cover bg-no-repeat'>
                <div className='bg-blackTransparent'>
                    <div className='flex flex-col justify-between md:flex-row-reverse pt-20 md:pt-52 pb-8 px-4 lg:pr-24'>
                        <motion.img
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            src={images.BLOGBANNERIMAGE}
                            className='w-full md:w-[50%] lg:w-[35%]'
                        />
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className='text-white flex flex-col gap-2 h-auto md:h-[500px] relative w-full md:w-[50%] items-start px-4 lg:pl-24 mt-6 md:mt-0'
                        >
                            <div className='capitalize font-bold text-4xl md:text-7xl leading-tight md:leading-snug'>{blogData.title}</div>
                            <div className='font-medium text-lg md:text-2xl text-start'>{blogData.description}</div>
                        </motion.div>
                    </div>
                </div>
            </div>

            <div className='bg-darkGreyPrimary'>
                <CompanyStats yellowRef={yellowRef} greenRef={greenRef} purpleRef={purpleRef} />

                <Heading
                    subTitle='Explore New Ideas for Your Business'
                    className='pt-8 heading-subtitle-blog'
                />

                {/* ✅ UPDATED FILTER BUTTONS */}
                <motion.div
                    className="flex flex-nowrap md:flex-wrap gap-3 mb-6 px-4 md:px-24 mt-14 overflow-x-auto"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* ALL BUTTON */}
                    <motion.button
                        className={`py-2 md:py-3 px-4 md:px-6 font-semibold text-sm md:text-lg whitespace-nowrap border-2 rounded-full transition-all duration-300
                        ${activeTab === "All"
                                ? "bg-yellow text-black border-yellow"
                                : "border-yellow text-yellow bg-transparent hover:bg-yellow hover:text-black"
                            }`}
                        onClick={() => setActiveTab("All")}
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                    >
                        All
                    </motion.button>

                    {/* CATEGORY BUTTONS */}
                    {categoryOptions.map((category) => {
                        return (
                            <motion.button
                                key={category.value}
                                className={`py-2 md:py-3 px-4 md:px-6 font-semibold text-sm md:text-lg whitespace-nowrap border-2 rounded-full transition-all duration-300
                                ${activeTab === category.value
                                        ? "bg-yellow text-black border-yellow"
                                        : "border-yellow text-yellow bg-transparent hover:bg-yellow hover:text-black"
                                    }`}
                                onClick={() => setActiveTab(category.value)}
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                {category.label}
                            </motion.button>
                        )
                    })}
                </motion.div>

                <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 md:px-20 xl:px-40 mt-14"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {filterBlogs.map((blog) => {
                        return (
                            <Link to={`/blog/${blog.id}`} className="h-full" key={blog.id}>
                                <motion.div
                                    className='border-2 border-[#444] rounded-2xl overflow-hidden p-4 flex flex-col gap-4 h-full'
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <img src={blog.imageUrl} className='w-full h-48 md:h-72 rounded-2xl bg-blackDark object-cover' />

                                    <div>
                                        {blog.categories.map((category) => (
                                            <div key={category} className='text-yellow font-semibold text-lg'>
                                                {categoryOptions.filter((cat) => cat.value === category)?.[0]?.label}
                                            </div>
                                        ))}

                                        <div>
                                            {(() => {
                                                const createdDate = formatDate(blog.createdAt);
                                                const updatedDate = formatDate(blog.updatedAt);
                                                return createdDate === updatedDate
                                                    ? `Created on: ${createdDate}`
                                                    : `Last Updated: ${updatedDate}`;
                                            })()}
                                        </div>
                                    </div>
                                    <div className='font-semibold text-xl line-clamp-2 min-h-[3.5rem]'>{blog.title}</div>
                                    <div
                                        className='text-md text-[#DCDCDC] line-clamp-3'
                                        dangerouslySetInnerHTML={{ __html: blog?.description || '' }}
                                    ></div>
                                </motion.div>
                            </Link>
                        )
                    })}
                </motion.div>
                <ContactHelp title="Let's connect & achieve your business goals together" buttonTitle='CONTACT US' />
                <Footer />
            </div>
        </div>
    )
}

export default BlogPage
